/**
 * App Switcher Component
 *
 * Displays a grid of available apps similar to Office 365's waffle menu.
 * Filters apps based on user permissions and shows recent/favorite apps.
 */

import { LitElement, html, css, PropertyValues } from 'lit';
import { customElement, property, state, query } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import type { App } from '../types';
import { portalAPI } from '../services/api';
import { eventBus, Events } from '../services/events';

@customElement('moya-app-switcher')
export class MoyaAppSwitcher extends LitElement {
  static styles = css`
    :host {
      display: inline-block;
      position: relative;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    }

    .trigger {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 40px;
      height: 40px;
      border: none;
      background: transparent;
      border-radius: 8px;
      cursor: pointer;
      color: currentColor;
      transition: background-color 0.15s ease;
    }

    .trigger:hover {
      background: rgba(255, 255, 255, 0.1);
    }

    /* Visible keyboard focus indicator (WCAG 2.4.7) */
    .trigger:focus-visible {
      outline: 2px solid #60a5fa;
      outline-offset: 2px;
      box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.5);
    }

    .trigger.open {
      background: rgba(255, 255, 255, 0.15);
    }

    .waffle-icon {
      display: grid;
      grid-template-columns: repeat(3, 6px);
      gap: 3px;
    }

    .waffle-dot {
      width: 6px;
      height: 6px;
      background: currentColor;
      border-radius: 1px;
    }

    .dropdown {
      position: absolute;
      top: calc(100% + 8px);
      left: 0;
      width: 384px;
      max-height: min(580px, calc(100vh - 80px));
      background: #1e293b;
      border: 1px solid rgba(255, 255, 255, 0.1);
      border-radius: 12px;
      box-shadow: 0 20px 50px rgba(0, 0, 0, 0.3);
      overflow-y: auto;
      overflow-x: hidden;
      z-index: 1000;
      display: none;
    }

    .dropdown.open {
      display: block;
    }

    .dropdown::-webkit-scrollbar {
      width: 6px;
    }

    .dropdown::-webkit-scrollbar-track {
      background: transparent;
    }

    .dropdown::-webkit-scrollbar-thumb {
      background: rgba(255, 255, 255, 0.2);
      border-radius: 3px;
    }

    .search-container {
      position: sticky;
      top: 0;
      z-index: 1;
      background: #1e293b;
      padding: 12px;
      border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    }

    .search-input {
      width: 100%;
      padding: 8px 12px;
      background: rgba(255, 255, 255, 0.05);
      border: 1px solid rgba(255, 255, 255, 0.1);
      border-radius: 8px;
      color: white;
      font-size: 14px;
      outline: none;
    }

    .search-input:focus {
      border-color: rgba(59, 130, 246, 0.5);
      background: rgba(255, 255, 255, 0.08);
    }

    .search-input::placeholder {
      color: rgba(255, 255, 255, 0.4);
    }

    .section {
      padding: 8px 0;
    }

    .section-title {
      padding: 4px 12px 8px;
      font-size: 11px;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      color: rgba(255, 255, 255, 0.5);
    }

    .apps-grid {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 4px;
      padding: 0 8px;
    }

    .app-item {
      display: flex;
      flex-direction: column;
      align-items: center;
      padding: 12px 8px;
      border-radius: 8px;
      cursor: pointer;
      transition: background-color 0.15s ease;
      text-decoration: none;
      color: inherit;
    }

    .app-item:hover {
      background: rgba(255, 255, 255, 0.1);
    }

    .app-item:focus-visible {
      outline: 2px solid #60a5fa;
      outline-offset: -2px;
      background: rgba(255, 255, 255, 0.1);
    }

    .app-item.kb-active {
      background: rgba(59, 130, 246, 0.18);
      outline: 2px solid #60a5fa;
      outline-offset: -2px;
    }

    .app-icon {
      width: 36px;
      height: 36px;
      border-radius: 8px;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 16px;
      font-weight: 600;
      color: white;
      margin-bottom: 6px;
    }

    .app-icon svg {
      width: 20px;
      height: 20px;
    }

    .app-icon-img {
      width: 24px;
      height: 24px;
      border-radius: 4px;
      object-fit: cover;
    }

    .app-name {
      font-size: 11px;
      color: rgba(255, 255, 255, 0.9);
      text-align: center;
      overflow: hidden;
      text-overflow: ellipsis;
      max-width: 100%;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      white-space: normal;
      line-height: 1.3;
      word-break: break-word;
    }

    .favorite-star {
      position: absolute;
      top: 2px;
      right: 2px;
      font-size: 10px;
      color: #fbbf24;
    }

    .app-item-wrapper {
      position: relative;
    }

    .empty-state {
      padding: 24px;
      text-align: center;
      color: rgba(255, 255, 255, 0.5);
      font-size: 14px;
    }

    .divider {
      height: 1px;
      background: rgba(255, 255, 255, 0.1);
      margin: 0 12px;
    }

    .footer {
      position: sticky;
      bottom: 0;
      z-index: 1;
      background: #1e293b;
      padding: 12px;
      border-top: 1px solid rgba(255, 255, 255, 0.1);
    }

    .view-all {
      display: block;
      width: 100%;
      padding: 8px;
      background: rgba(59, 130, 246, 0.1);
      border: 1px solid rgba(59, 130, 246, 0.3);
      border-radius: 8px;
      color: #60a5fa;
      font-size: 13px;
      font-weight: 500;
      text-align: center;
      text-decoration: none;
      cursor: pointer;
      transition: background-color 0.15s ease;
    }

    .view-all:hover {
      background: rgba(59, 130, 246, 0.2);
    }

    .view-all:focus-visible {
      outline: 2px solid #60a5fa;
      outline-offset: 2px;
    }

    .loading {
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 40px;
    }

    .spinner {
      width: 24px;
      height: 24px;
      border: 2px solid rgba(255, 255, 255, 0.1);
      border-top-color: #60a5fa;
      border-radius: 50%;
      animation: spin 0.8s linear infinite;
    }

    @keyframes spin {
      to {
        transform: rotate(360deg);
      }
    }

    @media (max-width: 640px) {
      .dropdown {
        width: min(384px, calc(100vw - 32px));
        max-height: calc(100vh - 80px);
      }

      .app-item {
        padding: 10px 6px;
        min-height: 44px;
      }
    }

    @media (max-width: 400px) {
      .dropdown {
        width: calc(100vw - 24px);
      }

      .apps-grid {
        grid-template-columns: repeat(2, 1fr);
      }
    }
  `;

  @property({ type: String }) token = '';
  @property({ type: String }) currentAppId = '';
  @property({ type: String }) portalUrl = 'https://login.moyasolutions.com';

  @state() private isOpen = false;
  @state() private _apps: App[] = [];
  @state() private recentApps: App[] = [];
  @state() private loading = false;
  @state() private searchQuery = '';
  @state() private error: string | null = null;
  @state() private activeIndex = -1;

  @query('.trigger') private triggerEl!: HTMLButtonElement;
  @query('.search-input') private searchInput!: HTMLInputElement;

  private clickOutsideHandler: ((e: MouseEvent) => void) | null = null;
  private hasLoadedFromAPI = false;

  // Getter that returns fallback apps if none are set
  get apps(): App[] {
    return this._apps.length > 0 ? this._apps : MoyaAppSwitcher.FALLBACK_APPS;
  }

  set apps(value: App[]) {
    this._apps = value;
  }

  connectedCallback() {
    super.connectedCallback();
    this.clickOutsideHandler = this.handleClickOutside.bind(this);
    document.addEventListener('click', this.clickOutsideHandler);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.clickOutsideHandler) {
      document.removeEventListener('click', this.clickOutsideHandler);
    }
  }

  protected updated(_changedProperties: PropertyValues) {
    // Load apps from API when dropdown is first opened
    // Only load once to avoid repeated API calls
    if (_changedProperties.has('isOpen') && this.isOpen && !this.hasLoadedFromAPI) {
      this.loadApps();
    }
    // Auto-focus the search input when the dropdown opens (keyboard UX)
    if (_changedProperties.has('isOpen') && this.isOpen) {
      requestAnimationFrame(() => this.searchInput?.focus());
    }
  }

  private handleClickOutside(e: MouseEvent) {
    if (this.isOpen && !this.contains(e.target as Node)) {
      this.isOpen = false;
      this.activeIndex = -1;
    }
  }

  // Fallback apps when portal API is not available
  private static readonly FALLBACK_APPS: App[] = [
    { id: 'endpoints', name: 'Endpoints', slug: 'endpoints', url: 'https://endpoints.moyasolutions.com', color: 'linear-gradient(135deg, #3b82f6, #1d4ed8)', description: 'Endpoint Management', icon: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></svg>' },
    { id: 'tickets', name: 'Support', slug: 'tickets', url: 'https://tickets.moyasolutions.com', color: 'linear-gradient(135deg, #f59e0b, #d97706)', description: 'Support Portal', icon: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z"/></svg>' },
    { id: 'tickets-agent', name: 'Agent', slug: 'tickets-agent', url: 'https://agent.moyasolutions.com', color: 'linear-gradient(135deg, #10b981, #047857)', description: 'Agent Dashboard', icon: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 015.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>' },
    { id: 'crm', name: 'CRM', slug: 'crm', url: 'https://crm.moyasolutions.com', color: 'linear-gradient(135deg, #ec4899, #be185d)', description: 'Customer Relationships', icon: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75"/></svg>' },
    { id: 'dashboard', name: 'Dashboard', slug: 'dashboard', url: 'https://dashboard.moyasolutions.com', color: 'linear-gradient(135deg, #f97316, #ea580c)', description: 'AI Agent Dashboard', icon: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></svg>' },
    { id: 'finance', name: 'Finance', slug: 'finance', url: 'https://finance.moyasolutions.com', color: 'linear-gradient(135deg, #06b6d4, #0891b2)', description: 'Financial Platform', icon: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"/></svg>' },
    { id: 'inventory', name: 'Inventory', slug: 'inventory', url: 'https://inventory.moyasolutions.com', color: 'linear-gradient(135deg, #14b8a6, #0d9488)', description: 'Infrastructure Inventory', icon: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="2" y="2" width="20" height="8" rx="2"/><rect x="2" y="14" width="20" height="8" rx="2"/><line x1="6" y1="6" x2="6.01" y2="6"/><line x1="6" y1="18" x2="6.01" y2="18"/></svg>' },
    { id: 'messenger', name: 'Messenger', slug: 'messenger', url: 'https://messenger.moyasolutions.com', color: 'linear-gradient(135deg, #a855f7, #7c3aed)', description: 'Team Messaging', icon: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg>' },
    { id: 'voice', name: 'Voice', slug: 'voice', url: 'https://voice.moyasolutions.com', color: 'linear-gradient(135deg, #6366f1, #4338ca)', description: 'Voice AI System', icon: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 1a3 3 0 00-3 3v8a3 3 0 006 0V4a3 3 0 00-3-3z"/><path d="M19 10v2a7 7 0 01-14 0v-2"/><line x1="12" y1="19" x2="12" y2="23"/><line x1="8" y1="23" x2="16" y2="23"/></svg>' },
    { id: 'admin', name: 'Admin', slug: 'admin', url: 'https://admin.moyasolutions.com', color: 'linear-gradient(135deg, #ef4444, #b91c1c)', description: 'Admin Portal', icon: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12.22 2h-.44a2 2 0 00-2 2v.18a2 2 0 01-1 1.73l-.43.25a2 2 0 01-2 0l-.15-.08a2 2 0 00-2.73.73l-.22.38a2 2 0 00.73 2.73l.15.1a2 2 0 011 1.72v.51a2 2 0 01-1 1.74l-.15.09a2 2 0 00-.73 2.73l.22.38a2 2 0 002.73.73l.15-.08a2 2 0 012 0l.43.25a2 2 0 011 1.73V20a2 2 0 002 2h.44a2 2 0 002-2v-.18a2 2 0 011-1.73l.43-.25a2 2 0 012 0l.15.08a2 2 0 002.73-.73l.22-.39a2 2 0 00-.73-2.73l-.15-.08a2 2 0 01-1-1.74v-.5a2 2 0 011-1.74l.15-.09a2 2 0 00.73-2.73l-.22-.38a2 2 0 00-2.73-.73l-.15.08a2 2 0 01-2 0l-.43-.25a2 2 0 01-1-1.73V4a2 2 0 00-2-2z"/><circle cx="12" cy="12" r="3"/></svg>' },
    { id: 'portal', name: 'Portal', slug: 'portal', url: 'https://login.moyasolutions.com', color: 'linear-gradient(135deg, #8b5cf6, #6d28d9)', description: 'App Portal', icon: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z"/></svg>' },
  ];

  private async loadApps() {
    if (this.loading) return;

    this.loading = true;
    this.error = null;
    this.hasLoadedFromAPI = true;

    try {
      console.log('[AppSwitcher] Loading apps from Portal API...');
      const dashboard = await portalAPI.getDashboard();
      console.log('[AppSwitcher] Loaded', dashboard.apps?.length || 0, 'apps from API');
      if (dashboard.apps && dashboard.apps.length > 0) {
        this.apps = dashboard.apps;
        this.recentApps = dashboard.recentApps?.slice(0, 3) || [];
      } else {
        console.log('[AppSwitcher] No apps returned from API, using fallback');
        this.apps = MoyaAppSwitcher.FALLBACK_APPS;
        this.recentApps = [];
      }
    } catch (e) {
      console.error('[AppSwitcher] Failed to load apps from Portal API, using fallback:', e);
      // Use fallback apps when API is not available
      this.apps = MoyaAppSwitcher.FALLBACK_APPS;
      this.recentApps = [];
      this.error = null; // Don't show error, we have fallback
    } finally {
      this.loading = false;
    }
  }

  private toggleDropdown(e: Event) {
    e.stopPropagation();
    this.isOpen = !this.isOpen;
    if (!this.isOpen) {
      this.activeIndex = -1;
    }
  }

  private isValidUUID(str: string): boolean {
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    return uuidRegex.test(str);
  }

  private async handleAppClick(app: App, e: Event) {
    e.preventDefault();

    // If it's the current app, just close the dropdown
    if (app.id === this.currentAppId) {
      this.isOpen = false;
      return;
    }

    // Check if this is a fallback app (non-UUID ID)
    // Fallback apps should navigate directly since the portal API requires UUID
    if (!this.isValidUUID(app.id)) {
      console.log('[AppSwitcher] Using direct navigation for fallback app:', app.name);

      // Dispatch custom event
      this.dispatchEvent(new CustomEvent('moya-app-switch', {
        bubbles: true,
        composed: true,
        detail: { app, ssoUrl: app.url },
      }));

      // Navigate directly to the app
      window.location.href = app.url;
      return;
    }

    try {
      const { sso_url } = await portalAPI.launchApp(app.id);

      // Dispatch custom event
      this.dispatchEvent(new CustomEvent('moya-app-switch', {
        bubbles: true,
        composed: true,
        detail: { app, ssoUrl: sso_url },
      }));

      // Also emit on event bus for internal components
      eventBus.emit(Events.APP_SWITCH_REQUEST, { app, ssoUrl: sso_url });

      // Navigate to the app
      window.location.href = sso_url;
    } catch (e) {
      console.error('[AppSwitcher] Failed to launch app via SSO, using direct URL:', e);
      // Fallback to direct navigation when API is unavailable
      if (app.url) {
        window.location.href = app.url;
      }
    }
  }

  private handleSearchInput(e: Event) {
    const input = e.target as HTMLInputElement;
    this.searchQuery = input.value.toLowerCase();
    this.activeIndex = -1;
  }

  /** Build the flat keyboard-navigation list (recents + filtered apps). */
  private get keyboardItems(): App[] {
    const items: App[] = [];
    if (this.recentApps.length > 0 && !this.searchQuery) {
      items.push(...this.recentApps);
    }
    items.push(...this.filteredApps);
    return items;
  }

  /** Keyboard handler for the trigger + dropdown (ESC/arrows/Enter/Home/End). */
  private handleKeydown(e: KeyboardEvent) {
    if (e.key === 'Escape') {
      if (this.isOpen) {
        e.preventDefault();
        this.isOpen = false;
        this.activeIndex = -1;
        this.triggerEl?.focus();
      }
      return;
    }

    // Open dropdown on ArrowDown when the trigger is focused
    if (!this.isOpen) {
      if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
        if (e.target === this.triggerEl) {
          e.preventDefault();
          this.isOpen = true;
        }
      }
      return;
    }

    const items = this.keyboardItems;
    if (items.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        this.activeIndex = Math.min(this.activeIndex + 1, items.length - 1);
        if (this.activeIndex < 0) this.activeIndex = 0;
        break;
      case 'ArrowUp':
        e.preventDefault();
        this.activeIndex = Math.max(this.activeIndex - 1, 0);
        break;
      case 'Home':
        e.preventDefault();
        this.activeIndex = 0;
        break;
      case 'End':
        e.preventDefault();
        this.activeIndex = items.length - 1;
        break;
      case 'Enter':
        if (this.activeIndex >= 0 && this.activeIndex < items.length) {
          e.preventDefault();
          this.handleAppClick(items[this.activeIndex], e);
        }
        break;
    }
  }

  private get filteredApps(): App[] {
    if (!this.searchQuery) {
      return this.apps;
    }
    return this.apps.filter(
      (app) =>
        app.name.toLowerCase().includes(this.searchQuery) ||
        (app.description?.toLowerCase().includes(this.searchQuery))
    );
  }

  // Map of Lucide icon names to inline SVG markup
  // Used when the Portal API returns icon names instead of raw SVG
  private static readonly LUCIDE_ICONS: Record<string, string> = {
    KeyRound: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M2.586 17.414A2 2 0 0 0 2 18.828V21a1 1 0 0 0 1 1h3a1 1 0 0 0 1-1v-1a1 1 0 0 1 1-1h1a1 1 0 0 0 1-1v-1a1 1 0 0 1 1-1h.172a2 2 0 0 0 1.414-.586l.814-.814a6.5 6.5 0 1 0-4-4z"/><circle cx="16.5" cy="7.5" r=".5" fill="currentColor"/></svg>',
    Monitor: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></svg>',
    TrendingUp: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="22 7 13.5 15.5 8.5 10.5 2 17"/><polyline points="16 7 22 7 22 13"/></svg>',
    ClipboardList: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="8" y="2" width="8" height="4" rx="1" ry="1"/><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/><path d="M12 11h4"/><path d="M12 16h4"/><path d="M8 11h.01"/><path d="M8 16h.01"/></svg>',
    Ticket: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M2 9a3 3 0 0 1 0 6v2a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-2a3 3 0 0 1 0-6V7a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2Z"/><path d="M13 5v2"/><path d="M13 17v2"/><path d="M13 11v2"/></svg>',
    Headset: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 11h3a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-5Zm0 0a9 9 0 1 1 18 0m0 0v5a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3Z"/><path d="M21 16v2a4 4 0 0 1-4 4h-5"/></svg>',
    Database: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><ellipse cx="12" cy="5" rx="9" ry="3"/><path d="M3 5V19A9 3 0 0 0 21 19V5"/><path d="M3 12A9 3 0 0 0 21 12"/></svg>',
    MessageCircle: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M7.9 20A9 9 0 1 0 4 16.1L2 22Z"/></svg>',
    Bell: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9"/><path d="M10.3 21a1.94 1.94 0 0 0 3.4 0"/></svg>',
    Users: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>',
    Receipt: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 2v20l2-1 2 1 2-1 2 1 2-1 2 1 2-1 2 1V2l-2 1-2-1-2 1-2-1-2 1-2-1-2 1Z"/><path d="M16 8h-6a2 2 0 1 0 0 4h4a2 2 0 1 1 0 4H8"/><path d="M12 17.5v-11"/></svg>',
    Phone: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/></svg>',
    LayoutDashboard: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect width="7" height="9" x="3" y="3" rx="1"/><rect width="7" height="5" x="14" y="3" rx="1"/><rect width="7" height="9" x="14" y="12" rx="1"/><rect width="7" height="5" x="3" y="16" rx="1"/></svg>',
    Settings: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z"/><circle cx="12" cy="12" r="3"/></svg>',
    // Common extras that might be used in future
    Shield: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z"/></svg>',
    Globe: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20"/><path d="M2 12h20"/></svg>',
    Server: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect width="20" height="8" x="2" y="2" rx="2" ry="2"/><rect width="20" height="8" x="2" y="14" rx="2" ry="2"/><line x1="6" x2="6.01" y1="6" y2="6"/><line x1="6" x2="6.01" y1="18" y2="18"/></svg>',
    Mail: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect width="20" height="16" x="2" y="4" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>',
    BarChart: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="12" x2="12" y1="20" y2="10"/><line x1="18" x2="18" y1="20" y2="4"/><line x1="6" x2="6" y1="20" y2="16"/></svg>',
  };

  private getAppIconType(app: App): 'svg' | 'url' | 'lucide' | 'letter' {
    if (!app.icon) return 'letter';
    if (app.icon.startsWith('<svg') || app.icon.startsWith('<SVG')) return 'svg';
    if (app.icon.startsWith('http') || app.icon.startsWith('/')) return 'url';
    if (app.icon.length <= 2) return 'letter';
    // Check if it's a Lucide icon name (PascalCase string)
    if (/^[A-Z][a-zA-Z]+$/.test(app.icon) && app.icon in MoyaAppSwitcher.LUCIDE_ICONS) return 'lucide';
    if (/^[A-Z][a-zA-Z]+$/.test(app.icon)) return 'lucide'; // Unknown Lucide name — fall back gracefully
    return 'svg'; // Assume SVG for other longer strings
  }

  private getAppColor(app: App): string {
    // Default gradient colors based on app name
    const colors = [
      'linear-gradient(135deg, #3b82f6, #1d4ed8)',
      'linear-gradient(135deg, #8b5cf6, #6d28d9)',
      'linear-gradient(135deg, #ec4899, #be185d)',
      'linear-gradient(135deg, #f97316, #c2410c)',
      'linear-gradient(135deg, #10b981, #047857)',
      'linear-gradient(135deg, #06b6d4, #0e7490)',
    ];

    if (app.color) {
      return app.color.startsWith('#') || app.color.startsWith('linear')
        ? app.color
        : `linear-gradient(135deg, ${app.color})`;
    }

    // Consistent color based on app name
    const index = app.name.charCodeAt(0) % colors.length;
    return colors[index];
  }

  private renderAppIcon(app: App) {
    const iconType = this.getAppIconType(app);
    if (iconType === 'svg') {
      return unsafeHTML(app.icon!);
    }
    if (iconType === 'lucide') {
      const svg = MoyaAppSwitcher.LUCIDE_ICONS[app.icon!];
      if (svg) return unsafeHTML(svg);
      // Unknown Lucide icon name — show first letter
      return app.name.charAt(0).toUpperCase();
    }
    if (iconType === 'url') {
      return html`<img class="app-icon-img" src="${app.icon}" alt="" @error="${(e: Event) => { (e.target as HTMLImageElement).replaceWith(document.createTextNode(app.name.charAt(0).toUpperCase())); }}" />`;
    }
    return app.icon || app.name.charAt(0).toUpperCase();
  }

  private renderApp(app: App, kbIndex: number) {
    const isCurrentApp = app.id === this.currentAppId;
    const isKbActive = kbIndex === this.activeIndex;
    const itemClasses = classMap({
      'app-item': true,
      'kb-active': isKbActive,
    });

    return html`
      <div class="app-item-wrapper">
        <a
          class="${itemClasses}"
          href="${app.url}"
          @click="${(e: Event) => this.handleAppClick(app, e)}"
          tabindex="${this.isOpen ? '0' : '-1'}"
          title="${app.description || app.name}"
          role="menuitem"
          aria-label="${app.name}${app.description ? ` — ${app.description}` : ''}${isCurrentApp ? ' (current app)' : ''}"
          aria-current="${isCurrentApp ? 'page' : 'false'}"
          style="${isCurrentApp ? 'opacity: 0.5;' : ''}"
        >
          <div
            class="app-icon"
            style="background: ${this.getAppColor(app)}"
            aria-hidden="true"
          >
            ${this.renderAppIcon(app)}
          </div>
          <span class="app-name">${app.name}</span>
        </a>
        ${app.isFavorite ? html`<span class="favorite-star" aria-label="Favorite">★</span>` : ''}
      </div>
    `;
  }

  render() {
    const triggerClasses = classMap({
      trigger: true,
      open: this.isOpen,
    });

    const dropdownClasses = classMap({
      dropdown: true,
      open: this.isOpen,
    });

    // Compute keyboard offset so kb-active highlight stays in sync between
    // the optional Recents section and All Apps section.
    const recentsCount = this.recentApps.length > 0 && !this.searchQuery ? this.recentApps.length : 0;

    return html`
      <button
        class="${triggerClasses}"
        @click="${this.toggleDropdown}"
        @keydown="${this.handleKeydown}"
        aria-label="Open app switcher"
        aria-expanded="${this.isOpen ? 'true' : 'false'}"
        aria-haspopup="menu"
        aria-controls="moya-app-switcher-menu"
      >
        <div class="waffle-icon" aria-hidden="true">
          ${[...Array(9)].map(() => html`<div class="waffle-dot"></div>`)}
        </div>
      </button>

      <div
        id="moya-app-switcher-menu"
        class="${dropdownClasses}"
        role="menu"
        aria-label="Available apps"
        @keydown="${this.handleKeydown}"
      >
        <div class="search-container">
          <input
            type="text"
            class="search-input"
            placeholder="Search apps..."
            .value="${this.searchQuery}"
            @input="${this.handleSearchInput}"
            aria-label="Search apps"
            autocomplete="off"
          />
        </div>

        ${this.loading
          ? html`
              <div class="loading" role="status" aria-live="polite" aria-label="Loading apps">
                <div class="spinner"></div>
              </div>
            `
          : this.error
          ? html`
              <div class="empty-state" role="alert">
                ${this.error}
                <button @click="${this.loadApps}" style="margin-top: 8px; cursor: pointer;">
                  Retry
                </button>
              </div>
            `
          : html`
              ${this.recentApps.length > 0 && !this.searchQuery
                ? html`
                    <div class="section" role="group" aria-labelledby="moya-app-switcher-recents">
                      <div class="section-title" id="moya-app-switcher-recents">Recent</div>
                      <div class="apps-grid">
                        ${this.recentApps.map((app, i) => this.renderApp(app, i))}
                      </div>
                    </div>
                    <div class="divider" aria-hidden="true"></div>
                  `
                : ''}

              <div class="section" role="group" aria-labelledby="moya-app-switcher-all">
                <div class="section-title" id="moya-app-switcher-all">
                  ${this.searchQuery ? 'Search Results' : 'All Apps'}
                </div>
                ${this.filteredApps.length > 0
                  ? html`
                      <div class="apps-grid">
                        ${this.filteredApps.map((app, i) => this.renderApp(app, recentsCount + i))}
                      </div>
                    `
                  : html`
                      <div class="empty-state">
                        ${this.searchQuery
                          ? 'No apps found'
                          : 'No apps available'}
                      </div>
                    `}
              </div>
            `}

        <div class="footer">
          <a href="${this.portalUrl}" class="view-all" tabindex="${this.isOpen ? '0' : '-1'}">
            View all apps
          </a>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'moya-app-switcher': MoyaAppSwitcher;
  }
}
